import { scrollEdges } from './lf-utils.js';

const CAROUSEL_STEP = 0.8;

/**
 * Horizontal scroll-snap row with optional prev/next buttons. Markup contract: the scroller is
 * `[data-lf-carousel-track]`; buttons are `[data-lf-carousel-prev]` and `[data-lf-carousel-next]`, optionally inside a
 * `[data-lf-carousel-controls]` wrapper that is hidden while everything fits. Buttons scroll by 80% of the track width
 * and disable at the ends; native scroll and snap handle swipe.
 */
class LfCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLElement | null} */
  #controls = null;
  /** @type {ResizeObserver | null} */
  #observer = null;
  #queued = false;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#track = this.querySelector('[data-lf-carousel-track]');
    if (!this.#track) return;
    this.#prev = this.querySelector('[data-lf-carousel-prev]');
    this.#next = this.querySelector('[data-lf-carousel-next]');
    this.#controls = this.querySelector('[data-lf-carousel-controls]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);

    // A row without buttons (the category row on desktop, for one) has no state to keep in sync: no scroll listener, no observer.
    if (!this.#prev && !this.#next && !this.#controls) return;
    this.#track.addEventListener('scroll', this.#queueUpdate, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.#observer = new ResizeObserver(this.#queueUpdate);
      this.#observer.observe(this.#track);
    }
    this.#update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.#track?.removeEventListener('scroll', this.#queueUpdate);
  }

  #queueUpdate = () => {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.#update();
    });
  };

  #update() {
    const track = this.#track;
    if (!track) return;
    const { overflows, atStart, atEnd } = scrollEdges(track.scrollLeft, track.clientWidth, track.scrollWidth);
    if (this.#controls) this.#controls.hidden = !overflows;

    // Enable first, then disable, so focus can hop to the live button instead of falling to the page.
    const states = /** @type {const} */ ([
      [this.#prev, atStart, this.#next],
      [this.#next, atEnd, this.#prev],
    ]);
    for (const [button, disabled] of states) if (button && !disabled) button.disabled = false;
    for (const [button, disabled, other] of states) {
      if (!button || !disabled) continue;
      if (document.activeElement === button && other && !other.disabled) other.focus({ preventScroll: true });
      button.disabled = true;
    }
  }

  /** @param {-1 | 1} direction */
  #scroll(direction) {
    const track = this.#track;
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * track.clientWidth * CAROUSEL_STEP, behavior: this.#motion.matches ? 'auto' : 'smooth' });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-lf-carousel-prev]')) this.#scroll(-1);
    else if (event.target.closest('[data-lf-carousel-next]')) this.#scroll(1);
  };

  /**
   * Centres a block selected in the theme editor without moving the page vertically.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const track = this.#track;
    const item = event.target;
    if (!track || !(item instanceof HTMLElement) || !track.contains(item)) return;
    const offset = item.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({ left: track.scrollLeft + offset - (track.clientWidth - item.offsetWidth) / 2, behavior: 'auto' });
  };
}

if (!customElements.get('lf-carousel')) customElements.define('lf-carousel', LfCarousel);
